<template>
   <!--Page alert + loader-->
   <PageStatus :alert="defaultAlert" :table-data="tableData" />


   <!--Import Data-->
   <ImportModal
      :form-import-data="formImportData"
      :alert="defaultAlert"
      @file-change="onFileImportChange"
      @import="importRecords"
      @download-template="downloadImportTemplate"
   >
      <!--Import Notes-->
      <div id="accordion">
         <div class="card">
            <div class="card-header">
               <a class="btn" data-bs-toggle="collapse" href="#collapseOne">
                  Import Process
               </a>
            </div>
            <div id="collapseOne" class="collapse show" data-bs-parent="#accordion">
               <div class="card-body">
                  <ol>
                     <li>Permissions are <b>one list for the whole system</b>. They can only be imported from the <b>platform company</b>.</li>
                     <li>Use this to copy your own permission list, for example from your local PC to the live server. Do <b>not</b> import permissions from another ERP: their keys will not match this system's code.</li>
                     <li>Create the <b>categories first</b> (Permission Categories page). The Category column holds the category's <b>name</b>, e.g. Users, so it works on any server.</li>
                     <li>Click <b>Download Import Template</b> for the right columns, or use a file from <b>Export to Excel</b> on another server: it has the same columns.</li>
                     <li>Permission key: starts with a letter, then only letters, numbers, - _ or . (no spaces).</li>
                     <li>Rows whose permission key already exists are skipped and listed after the import.</li>
                  </ol>
               </div>
            </div>
         </div>
      </div>
      <!--End Import Notes-->
   </ImportModal>
   <!--Import Data-->

         <!--Filter Data Modal (shared: FilterModal.vue)-->
   <FilterModal
      :draft="filterDraft"
      v-model:search-by="tableData.searchBy"
      :view-options="viewOptions"
      :search-by-options="searchByOptions"
      :alert="defaultAlert"
      @search-by-change="changePlaceholder()"
      @apply="applyFilters"
      @clear="clearFilters"
   />
   <!--End Filter Data Modal-->


   <!-- The Modal -->
   <RecordFormModal
      :title="isEditing ? 'Update Permission' : 'Add Permission'"
      title-icon="bi bi-key"
      :is-editing="isEditing"
      :is-saving="isSaving"
      :alert="defaultAlert"
      @save="saveOrEditRecord"
      @sample="addSampleData"
      @reset="resetForm"
   >
      <!--Below id data id. User id is accessed directly in computed properties inside formData_fields function-->
      <HiddenField v-model="formInputs.id" />

      <!-- Row 1: Name & Category -->
      <div class="row g-3 mb-0">
         <FormField
            v-model="formInputs.name"
            col="col-md-6"
            label="Name"
            icon="fa fa-tag"
            placeholder="e.g. View Invoices"
            tooltip="What this permission allows, e.g. View Invoices"
            required
            @input="handleInputChange"
            @change="handleInputChange"
         />
         <SelectField
            v-model="formInputs.category"
            col="col-md-6"
            name="category"
            label="Category"
            icon="fa fa-folder"
            :options="categoryOptions"
            :reduce="category => category.id"
            option-label="category_name"
            placeholder="Select..."
            tooltip="Please select Category."
            :submitted="submitted"
            required
            @change="$clearValidationAlert('#recordForm')"
         />
      </div>

      <!-- Row 2: Permission Key -->
      <div class="row g-3 mb-0">
         <FormField
            v-model="formInputs.permission"
            id="permissionInput"
            col="col-md-12"
            label="Permission Key"
            icon="fa fa-key"
            placeholder="e.g. invoices.view (lowercase, letters, numbers, _ . no spaces)"
            tooltip="The key the code checks, e.g. invoices.view"
            :hint="isEditing ? 'The permission key cannot be changed; the code uses it.' : ''"
            :readonly="isEditing"
            required
            @input="handleInputChange"
            @change="handleInputChange"
         />
      </div>
   </RecordFormModal>

   
   <h4 class="sa-core-color fw-semibold mb-2 d-flex align-items-center flex-wrap gap-2">
      <span><i :class="[pageIcon, 'me-2 opacity-75']"></i>{{ pageTitle }}</span>
      <!-- View switch -->
      <span class="btn-group btn-group-sm ms-auto">
         <button type="button" class="btn" :class="viewMode === 'grouped' ? 'btn-primary' : 'btn-outline-secondary'" @click="setViewMode('grouped')">
            <i class="fa fa-folder-open me-1"></i> Grouped
         </button>
         <button type="button" class="btn" :class="viewMode === 'table' ? 'btn-primary' : 'btn-outline-secondary'" @click="setViewMode('table')">
            <i class="fa fa-table me-1"></i> Table
         </button>
      </span>
   </h4>
   <!--Tables-->
   <div class="card glass mt-0 bg-sa-card">
      <div class="card-body bg-sa-card-body p-0">
                  <!-- Table action row (shared: TableToolbar.vue) -->
         <TableToolbar
            can-add
            :filter-count="activeFilterCount"
            :columns="viewMode === 'table' ? tableColumns : []"
            v-model:hidden-columns="hiddenColumns"
            :selected-count="viewMode === 'table' ? tableData.selectedRecordIds.length : 0"
            :deleted-view="tableData.recordFilter === 'deletedone'"
            :can-delete="$hasPermission('permissions.delete')"
            @add="clearForm()"
            @filter="openFilters"
            @clear-selection="clearSelection"
            @bulk-action="runBulkAction"
         >
            <!-- More menu -->
            <template #more>
               <li><h6 class="dropdown-header">Data</h6></li>
               <li>
                  <button type="button" class="dropdown-item" data-bs-toggle="modal" data-bs-target="#importModal">
                     <i class="fa fa-upload fa-fw me-1"></i> Import
                  </button>
               </li>
               <li>
                  <button type="button" class="dropdown-item" @click="exportRecords('xls')">
                     <i class="fa fa-file-excel-o fa-fw me-1 text-success"></i> Export to Excel
                  </button>
               </li>
               <li>
                  <button type="button" class="dropdown-item" @click="exportRecords('csv')">
                     <i class="fa fa-file-text-o fa-fw me-1 text-success"></i> Export to CSV
                  </button>
               </li>
               <li>
                  <button type="button" class="dropdown-item" @click="exportRecords('pdf')">
                     <i class="fa fa-file-pdf-o fa-fw me-1 text-danger"></i> Export to PDF
                  </button>
               </li>
            </template>
         </TableToolbar>
         <!-- Table Search Panel -->

         <!-- Table Search Panel -->
         <SearchBox
            v-show="viewMode === 'table'"
            v-model:search-query="tableData.searchQuery"
            v-model:records-per-page="tableData.recordsPerPage"
            :placeholder="tableData.searchPlaceHolderText"
            @search-input="onSearchInput"
            @search-click="onSearchButtonClick"
            @per-page-change="onRecordsPerPageChange"
         />
         <!-- What the table is currently showing -->
         <ActiveFilters
            v-show="viewMode === 'table'"
            :chips="activeFilterChips"
            @remove="removeFilterChip"
            @clear="clearAllFilterChips"
         />
         <!-- Grouped view: one row per category, + shows its permissions -->
         <div v-if="viewMode === 'grouped'">
            <div class="d-flex flex-wrap align-items-center gap-2 p-1 bg-table-search-panel">
               <div class="input-group input-group-sm cell-max-360">
                  <span class="input-group-text"><i class="fa fa-search"></i></span>
                  <input v-model="groupSearch" type="search" class="form-control" placeholder="Find a permission (name or key)...">
               </div>
               <small class="sa-core-color">{{ groupedTotal }} permission(s) in {{ groups.length }} categories</small>
               <div class="ms-auto d-flex gap-1">
                  <button type="button" class="btn btn-sm btn-outline-secondary" @click="setAllGroups(true)">Expand all</button>
                  <button type="button" class="btn btn-sm btn-outline-secondary" @click="setAllGroups(false)">Collapse all</button>
               </div>
            </div>

            <div class="table-responsive">
               <table class="table table-hover align-middle mb-0">
                  <thead>
                     <tr>
                        <th class="col-w-40"><span class="visually-hidden">Open</span></th>
                        <th>Category</th>
                        <th class="text-center">Permissions</th>
                        <th class="text-end">Action</th>
                     </tr>
                  </thead>
                  <tbody>
                     <tr v-if="groupsLoading">
                        <td colspan="4" class="text-center"><img src="/img/progress.gif" height="12" class="me-1" alt=""> Loading...</td>
                     </tr>

                     <template v-for="group in filteredGroups" :key="group.id">
                        <!-- Category row -->
                        <tr class="clickable-row" @click="toggleGroup(group)">
                           <td class="expand-cell">
                              <i class="fa fa-lg" :class="isGroupOpen(group) ? 'fa-minus-circle text-danger' : 'fa-plus-circle sa-core-color'"></i>
                           </td>
                           <td class="fw-semibold">{{ group.category_name }}</td>
                           <td class="text-center"><span class="badge bg-secondary">{{ group.permissions.length }}</span></td>
                           <td class="text-end">
                              <button v-if="group.id && tableData.recordFilter !== 'deletedone'" type="button" class="btn btn-success btn-sm" @click.stop="addToCategory(group)">
                                 <i class="fa fa-plus"></i> Add here
                              </button>
                           </td>
                        </tr>

                        <!-- Its permissions -->
                        <tr v-if="isGroupOpen(group)">
                           <td class="p-0"></td>
                           <td colspan="3" class="p-0">
                              <table class="table table-sm mb-2">
                                 <tbody>
                                    <tr v-for="(p, i) in group.permissions" :key="p.id"
                                        :data-record-id="p.id"
                                        :class="{ 'row-just-saved': isJustSaved(p) }">
                                       <td class="col-w-40">{{ i + 1 }}</td>
                                       <td>
                                          {{ p.name }}
                                          <div class="small"><code>{{ p.permission }}</code></div>
                                       </td>
                                       <td class="hide-sm-td small">{{ p.permission_number }}</td>
                                       <td class="text-end text-nowrap">
                                          <button v-if="p.is_deleted == 0" type="button" class="btn btn-sm btn-outline-primary me-1"
                                                  data-bs-toggle="modal" data-bs-target="#recordFormModal" @click="editRecord(p)">
                                             <i class="fa fa-edit"></i><span class="d-none d-md-inline"> Edit</span>
                                          </button>
                                          <button v-if="$hasPermission('permissions.delete') && p.is_deleted == 0" type="button" class="btn btn-sm btn-outline-danger"
                                                  @click="deleteFromGroup(p, 'soft')">
                                             <i class="fa fa-trash"></i><span class="d-none d-md-inline"> Delete</span>
                                          </button>
                                          <button v-if="$hasPermission('permissions.delete') && p.is_deleted == 1" type="button" class="btn btn-sm btn-outline-success"
                                                  @click="deleteFromGroup(p, 'restore')">
                                             <i class="fa fa-undo"></i> Restore
                                          </button>
                                       </td>
                                    </tr>
                                    <tr v-if="group.permissions.length === 0">
                                       <td colspan="4" class="text-muted small sa-core-color">No permissions in this category yet.</td>
                                    </tr>
                                 </tbody>
                              </table>
                           </td>
                        </tr>
                     </template>

                     <!-- Nothing to show: the empty message in the middle -->
                     <tr v-if="!groupsLoading && filteredGroups.length === 0" class="table-filler">
                        <td colspan="4" class="text-center align-middle h-240">
                           <TableEmptyState
                              :has-filters="!!groupSearch.trim() || tableData.recordFilter !== 'all'"
                              :can-add="true"
                              :title="groupSearch.trim() || tableData.recordFilter !== 'all' ? '' : 'No permissions yet'"
                              :message="groupSearch.trim() || tableData.recordFilter !== 'all' ? '' : 'Add the permissions your pages check, e.g. invoices.view.'"
                              icon="fa fa-key"
                              @add="clearForm()"
                              @clear="clearGroupedFilters"
                           />
                        </td>
                     </tr>
                  </tbody>
               </table>
            </div>
         </div>
         <!-- End Grouped view -->

         <!-- Table -->
         <div v-if="viewMode === 'table'" class="table-responsive">
            
            <table
    class="table table-hover align-middle mb-0"
    id="bootstrapTable"
    tabindex="0"
    @keydown.up="$moveRowSelection(-1, $event)"
    @keydown.down="$moveRowSelection(1, $event)"
    @keydown.enter="$onTableEnter($event)"
>
      <thead>
      <tr>
         <th>
            <input type="checkbox" v-model="tableData.selectAll" @change="toggleSelectAll" />
         </th>
         <th class="hide-sm-td"><span class="visually-hidden">Details</span></th>
         <th class="button hide-sm-td">No</th>
         <SortableTh v-if="isColumnVisible('name')" column="name" :table-data="tableData" @sort="orderBy">Name</SortableTh>
         <SortableTh v-if="isColumnVisible('permission')" column="permission" :table-data="tableData" @sort="orderBy" class="hide-sm-td">Permission</SortableTh>
         <SortableTh v-if="isColumnVisible('category')" column="category" :table-data="tableData" @sort="orderBy" class="hide-sm-td">Category</SortableTh>
         <SortableTh v-if="isColumnVisible('date_created')" column="date_created" :table-data="tableData" @sort="orderBy" class="hide-sm-td">Date Created</SortableTh>
         <th class="button">Action</th>
      </tr>
   </thead>

   <tbody>
      <template v-for="(record, index) in tableData.records" :key="record.id">

         <!-- Main row -->
         <tr class="clickable-row"
             :data-record-id="record.id"
             :class="{ 'active-row main-row': tableData.selectedRecordId === record.id, 'row-just-saved': isJustSaved(record) }"
             @click="$onRowClick(record, $event)"
             @dblclick="$onRowDblClick(record, $event)"
         >
            <td>
               <input type="checkbox" v-model="tableData.selectedRecordIds" :value="record.id" />
            </td>
            <td class="expand-cell hide-sm-td"
                :aria-expanded="expandedRows[record.id] ? 'true' : 'false'"
                @click="$toggleDetails(record.id)">
               <i class="fa fa-lg expand-icon ms-1"
                  :class="expandedRows[record.id] ? 'fa-minus-circle text-danger' : 'fa-plus-circle sa-core-color'"
                  :style="expandedRows[record.id] ? null : { color: 'inherit' }"></i>
            </td>
            <td class="hide-sm-td">{{ (tableData.currentPage - 1) * tableData.recordsPerPage + index + 1 }}</td>
            <td v-if="isColumnVisible('name')">
               <!-- Phones: + / − and the number go in front of the name (their columns are hidden) -->
               <span
                  class="show-sm-inline expand-cell me-2"
                  :aria-expanded="expandedRows[record.id] ? 'true' : 'false'"
                  @click="$toggleDetails(record.id)"
               >
                  <i class="fa fa-lg expand-icon"
                     :class="expandedRows[record.id] ? 'fa-minus-circle text-danger' : 'fa-plus-circle sa-core-color'"
                     :style="expandedRows[record.id] ? null : { color: 'inherit' }"></i>
               </span>
               <span class="show-sm-inline">{{ (tableData.currentPage - 1) * tableData.recordsPerPage + index + 1 }}. </span>{{ record.name }}
            </td>
            <td v-if="isColumnVisible('permission')" class="hide-sm-td"><code>{{ record.permission }}</code></td>
            <td v-if="isColumnVisible('category')" class="hide-sm-td">
               <span class="badge bg-secondary">{{ record.permission_cat_name || '-' }}</span>
            </td>
            <td v-if="isColumnVisible('date_created')" class="hide-sm-td">{{ $ConvertToLocalDate(record.date_created) }}</td>
            <td>
               <RowActions
                  :record="record"
                  :modal-id="'deleteCrudModal' + record.id"
                  :can-soft-delete="$hasPermission('permissions.delete')"
                  :can-restore="$hasPermission('permissions.delete')"
                  @edit="editRecord"
                  @delete-type="setDeleteType"
               />

               <!--Delete Modal-->
               <DeleteModal
                  :modal-id="'deleteCrudModal' + record.id"
                  :title="deleteModalTitle"
                  :message="deleteModalMessage"
                  @confirm="deleteRecord(record.id)"
               />
               <!--End Delete Modal-->
            </td>
         </tr>

         <!-- Hidden expanded row -->
         <tr class="collapse-row">
            <td class="p-0"></td>
            <td class="p-0 hide-sm-td"></td>
            <td colspan="6" class="p-0">
               <div :id="'tableDetails' + record.id" class="collapse table-collapse table-details">
                  <div class="p-3 details-list">
                     <div><i class="fa fa-hashtag"></i><strong>Permission Number:</strong> {{ record.permission_number }}</div>
                     <div><i class="fa fa-user"></i><strong>Creator Name:</strong> {{ [record.user_first_name, record.user_middle_name].filter(Boolean).join(' ') }}</div>
                     <div><i class="fa fa-building"></i><strong>Creator Company:</strong> {{ record.company_name }}</div>
                     <div><i class="fa fa-sitemap"></i><strong>Creator Branch:</strong> {{ record.branch_name }}</div>

                     <!-- Shown on phones, or always when the column is hidden (Columns button) -->
                     <div :class="{ 'show-sm-td': isColumnVisible('name') }"><i class="fa fa-tag"></i><strong>Name:</strong> {{ record.name }}</div>
                     <div :class="{ 'show-sm-td': isColumnVisible('permission') }"><i class="fa fa-key"></i><strong>Permission:</strong> <code>{{ record.permission }}</code></div>
                     <div :class="{ 'show-sm-td': isColumnVisible('category') }"><i class="fa fa-folder"></i><strong>Category:</strong> {{ record.permission_cat_name || '-' }}</div>
                     <div :class="{ 'show-sm-td': isColumnVisible('date_created') }"><i class="fa fa-calendar"></i><strong>Date Created:</strong> {{ $ConvertToLocalDate(record.date_created) }}</div>

                     <!-- Always in details -->
                     <div v-if="$hasPermission('view-location')"><i class="fa fa-location-arrow"></i><strong>Created From:</strong> {{ record.current_location }}</div>
                     <div><i class="fa fa-edit"></i><strong>Updated By:</strong> {{ record.updatedby_firstname }}</div>
                     <div><i class="fa fa-calendar"></i><strong>Date Updated:</strong> {{ this.$ConvertToLocalDate(record.date_updated) }}</div>
                     <div v-if="$hasPermission('view-ip-address')"><i class="fa fa-desktop"></i><strong>IP Address:</strong> {{ record.ip_address }}</div>
                  </div>
               </div>
            </td>
         </tr>

      </template>

      <!-- No rows: blank while loading, then the empty message in the middle -->
      <tr v-if="tableData.records.length === 0" class="table-filler">
         <td colspan="8" class="text-center align-middle" :style="{ height: fullPageHeight() }">
            <TableEmptyState
               v-if="!tableLoading"
               :has-filters="activeFilterCount > 0 || !!tableData.appliedSearchQuery"
               :can-add="true"
               :title="activeFilterCount > 0 || tableData.appliedSearchQuery ? '' : 'No permissions yet'"
               :message="activeFilterCount > 0 || tableData.appliedSearchQuery ? '' : 'Add the permissions your pages check, e.g. invoices.view.'"
               icon="fa fa-key"
               @add="clearForm()"
               @clear="clearAllFilterChips"
            />
         </td>
      </tr>
   </tbody>

   <tfoot class="table-light">
      <tr>
         <td colspan="8">
            <!-- Pagination (hidden when there are no rows; its space is kept) -->
            <div v-if="tableData.records.length === 0 && tableData.currentPage <= 1" class="pagination-placeholder"></div>
            <Pagination
               v-else
               :currentPage="tableData.currentPage"
               :totalPages="tableData.totalPages"
               :hasNextPage="tableData.hasNextPage"
               :totalRecords="tableData.totalRecords"
               :startRecord="tableData.startRecord"
               :endRecord="tableData.endRecord"
               :showFullPagination="tableData.totalRecords > 0"
               :lastKnownPage="lastKnownPage"
               @page-changed="fetchRecords"
               @loaderMessage="updateLoaderMessage"
               @last-page-request="goToLastPage"
            />
         </td>
      </tr>
   </tfoot>
</table>

         </div>
         <!-- Bottom Controls: Delete & Pagination -->
      </div>
   </div>
   <!--End Tables-->
</template>
<script>
import PageStatus from "../../../components/admin/PageStatus.vue";
import TableToolbar from "../../../components/admin/TableToolbar.vue";
import FilterModal from "../../../components/admin/FilterModal.vue";
import ActiveFilters from "../../../components/admin/ActiveFilters.vue";
import Pagination from "../../../components/admin/Pagination.vue";
import TableEmptyState from "../../../components/admin/TableEmptyState.vue";
import SortableTh from "../../../components/admin/SortableTh.vue";
import DeleteModal from "../../../components/admin/DeleteModal.vue";
import RowActions from "../../../components/admin/RowActions.vue";
import SearchBox from "../../../components/admin/SearchBox.vue";
import ImportModal from "../../../components/admin/ImportModal.vue";
import RecordFormModal from "../../../components/admin/RecordFormModal.vue";
import FormField from "../../../components/admin/FormField.vue";
import SelectField from "../../../components/admin/SelectField.vue";
import HiddenField from "../../../components/admin/HiddenField.vue";
import permissionsMixin from "./mixins/permissionsMixin.js";
import tableMixin from "@/mixins/tableMixin.js";
import axios from 'axios';


export default {
   name: 'Permissions',
   mixins: [tableMixin, permissionsMixin], // shared table methods first, then Permissions' own
   components: {
      PageStatus,
      TableToolbar,
      FilterModal,
      ActiveFilters,
      Pagination,
      TableEmptyState,
      SortableTh,
      DeleteModal,
      RowActions,
      SearchBox,
      ImportModal,
      RecordFormModal,
      FormField,
      SelectField,
      HiddenField,
   },
   data() {
      return {
         // Standard data (isSaving, tableData, defaultAlert, formImportData ...) comes from tableMixin
         standardPageSetup: true,          // tableMixin sets up Enter-to-save and table shortcuts
         apiUrl: "/users/api_permissions.php", // used by tableMixin's fetchRecords, delete, import, export
         importTemplateFileName: "permissions_import_template.xlsx",
         exportFileName: "permissionsExport", // name of the exported file
         columnsKey: "permissions",        // saves this page's hidden columns (Columns button)
         formInputs: this.getDefaultFormInputs(),
         pageIcon: 'fa fa-key', // same icon as this page in the sidebar
         // Filter window: View
         viewOptions: [
            { label: "All Records", value: "all" },
            { label: "Records By Me", value: "byme" },
            { label: "Records Deleted", value: "deletedone" },
         ],
         // Filter window: Search By
         searchByOptions: [
            { label: "Name", value: "name" },
            { label: "Permission Key", value: "permission" },
            { label: "Permission Number", value: "permission_number" },
         ],
         categoryOptions: [],
         // Grouped view
         viewMode: (() => { try { return localStorage.getItem('permissionsViewMode') || 'grouped'; } catch (e) { return 'grouped'; } })(),
         groups: [],
         groupSearch: '',
         expandedGroups: {},
         groupsLoading: false
      };
   },
   async created() {      
      //alert(this.$store.state.isLoggedIn);
      //this.retriveUserDetails();      
      this.fetchRecords(
                this.tableData.currentPage,
                this.tableData.currentSortColumn,
                "DESC",
                this.tableData.startDate,
                this.tableData.endDate,
                this.$loaderMessages.loaderMessageDefault,
            );
      await this.fetchPermissionCategories(); // the grouped list loads with the table (afterFetch)
      this.$checkIfCanFetchLocation();//Only applies if location is enabled for user. If not Log user out for him to enable location 
   },
   computed: {
    // Page heading that follows the chosen filter.
    pageTitle() {
        const titles = {
            all: "Permissions",
            byme: "Permissions By Me",
            deletedone: "Permissions Deleted Records",
        };
        return titles[this.tableData.recordFilter] || "Permissions";
    },
    // Columns the user can show or hide (Columns button, Table view)
    tableColumns() {
        return [
            { key: "name", label: "Name" },
            { key: "permission", label: "Permission" },
            { key: "category", label: "Category" },
            { key: "date_created", label: "Date Created" },
        ];
    },
    // Search matches anywhere in the name, key, number or category name
    filteredGroups() {
        const q = this.groupSearch.trim().toLowerCase();
        if (!q) return this.groups;

        return this.groups
            .map(g => {
                const categoryMatches = (g.category_name || '').toLowerCase().includes(q);
                const permissions = categoryMatches
                    ? g.permissions
                    : g.permissions.filter(p =>
                        (p.name || '').toLowerCase().includes(q) ||
                        (p.permission || '').toLowerCase().includes(q) ||
                        (p.permission_number || '').toLowerCase().includes(q)
                    );
                return { ...g, permissions };
            })
            .filter(g => g.permissions.length > 0);
    },
    groupedTotal() {
        return this.groups.reduce((total, g) => total + g.permissions.length, 0);
    },
   },
   methods: {
      getDefaultFormInputs(){
         return {
            //Form Data
            id: "",
            category: "",
            name: "",
            permission: ""
         };
      },
            // Table defaults: the standard ones (tableMixin) + this page's own
      getDefaultTableData(){
         return {
            ...this.baseTableData(),
            searchBy: 'name',
            searchPlaceHolderText: 'Search by Name',
         };
      },
      addSampleData() {
         // Random suffix so saving the sample twice doesn't clash on the permission key
         const suffix = Math.floor(100 + Math.random() * 900);

         this.formInputs = {
            ...this.getDefaultFormInputs(),
            name: "View Sample Reports " + suffix,
            category: this.categoryOptions.length ? this.categoryOptions[0].id : "",
            permission: "sample_reports_" + suffix + ".view", // resource.action style
         };

         this.submitted = false;
         this.$clearValidationStates("#recordForm");
         this.$scrollToTopModal("recordFormModal");
      },
      async fetchPermissionCategories() {
          try {
              const response = await axios.get(
                  `${this.apiUrl}?action=fetchPermissionCategories`,
                  this.$handleConfig()
              );

              if (response.data.status === "success") {
                  this.categoryOptions = response.data.results;
              }
          } catch (error) {
              console.error("Error fetching permission categories:", error);
          }
      },
      saveOrEditRecord() {    
         if (this.isSaving) return;
         this.isSaving = true;
        
        try {
         this.$scrollToTopModal("recordFormModal"); 
         this.submitted = true;
         const formIsValid = this.$validateForm('#recordForm');
         if (!formIsValid) {
           this.$playErrorSound();
           this.handleAlert("starting", "Please correct the highlighted fields.", "danger", "modal");
           this.isSaving = false; 
            return; 
          }

         this.handleAlert("starting","Please wait submiting","info","modal");

         let formData = this.formData_fields();

         if (!this.formInputs.id) {
            this.tableData.currentPage = 1;
         }

         const start = performance.now(); // Start time
         // Perform the POST request
         axios.post(this.apiUrl, formData, this.$handleConfig())
            .then((response) => {     
               if (response.data.status =='success') {
                  const end = performance.now(); // End time
                  const duration = end - start;
                  console.log("Time taken to receive:" + duration.toFixed(2));
                  this.handleSaveOrEditSuccess(response); // reloads the table, and the grouped list with it
               } else {
                   this.handleError("starting", response.data.message[0], "danger", "modal");
               }
            })
            .catch((error) => {
               if (error.message === 'Network Error') {
                  this.handleAlert("ending","No internet connection. Please check your network.","danger","modal");
                } else {
                  this.handleAlert("ending","Something went wrong. Please try again later.","danger","modal");
                }
               //this.handleError("starting",error,"danger","modal");
         })
          .finally(() => {
              this.isSaving = false;
          });
          } catch (error) {
            this.isSaving = false;
            throw error;
          }
      },
      formData_fields(){
         let formData = new FormData();
         formData.append("id", this.formInputs.id);
         formData.append("permission", this.formInputs.permission);
         formData.append("category", this.formInputs.category);
         formData.append("name", this.formInputs.name);
         formData.append("latitude", this.$store.state.latitude);
         formData.append("longitude", this.$store.state.longitude);
         formData.append('action', 'saveOrEditRecord');
         return formData;
      },
      editRecord(records) {
         this.isEditing = true;
         this.$clearValidationStates('#recordForm');
         this.formInputs.id = records.id ?? '';
         this.formInputs.permission = records.permission ?? '';
         this.formInputs.category = records.category ?? '';
         //$(this.$refs.category).val(this.formInputs.category).trigger('change');
         this.formInputs.name = records.name ?? '';
         this.$clearValidationStates('#recordForm');
      },

            // After the table loads (tableMixin calls this): in Grouped view, reload the groups too.
      // So save, delete, import and the View filter refresh both views.
      afterFetch() {
         if (this.viewMode === 'grouped') this.fetchGroupedPermissions();
      },

      /* ---------- Grouped view ---------- */
      async fetchGroupedPermissions() {
         this.groupsLoading = true;
         try {
            const { data } = await axios.get(
               `${this.apiUrl}?action=fetchGroupedPermissions&filter=${encodeURIComponent(this.tableData.recordFilter)}`,
               this.$handleConfig()
            );

            if (data.status === "success") {
               this.groups = data.results.groups || [];

               // Just saved: open its category so the blinking row can be seen
               if (this.justSavedRecordId) {
                  const group = this.groups.find(g => g.permissions.some(p => Number(p.id) === Number(this.justSavedRecordId)));
                  if (group) {
                     this.expandedGroups = { ...this.expandedGroups, [group.id]: true };
                  }
               }
            } else if (["JWT_EXPIRED", "CSRF_INVALID", "AUTH_REQUIRED"].includes(data?.code)) {
               this.$forceLogOut("danger", data.message);
            } else {
               this.handleAlert("ending", data.message, "danger", "page");
            }
         } catch (error) {
            this.handleAlert("ending", error.message === 'Network Error'
               ? "No internet connection. Please check your network."
               : "Something went wrong. Please try again later.", "danger", "page");
         } finally {
            this.groupsLoading = false;
         }
      },
      setViewMode(mode) {
         this.viewMode = mode;
         try { localStorage.setItem('permissionsViewMode', mode); } catch (e) { /* not available */ }
         if (mode === 'grouped') this.fetchGroupedPermissions();
      },
      // While searching, every category with a match is open
      isGroupOpen(group) {
         return this.groupSearch.trim() !== '' || !!this.expandedGroups[group.id];
      },
      toggleGroup(group) {
         this.expandedGroups = { ...this.expandedGroups, [group.id]: !this.expandedGroups[group.id] };
      },
      setAllGroups(open) {
         this.expandedGroups = open ? Object.fromEntries(this.groups.map(g => [g.id, true])) : {};
      },
      // Grouped view empty message "Clear filters": empty the search box, and the View filter if one is on
      clearGroupedFilters() {
         this.groupSearch = '';
         if (this.tableData.recordFilter !== 'all') this.clearAllFilterChips(); // reloads both views
      },
      // "Add here": form opens with this category already chosen
      addToCategory(group) {
         this.clearForm();
         this.formInputs.category = group.id;
         this.$showModal("recordFormModal");
      },
      deleteFromGroup(permission, type) {
         const question = type === 'restore'
            ? `Restore "${permission.name}"?`
            : `Delete "${permission.name}"? Roles and plans using it will stop seeing it.`;
         if (!confirm(question)) return;

         this.setDeleteType(type);
         this.deleteRecord(permission.id);
      }      
   },
}; 
</script>